import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Link, useNavigate } from "react-router-dom"
import "../../../node_modules/bootstrap/dist/css/bootstrap.min.css"
import TableContainer from "../../components/Common/TableContainer"
import Moment from "moment"

//import components
import Breadcrumbs from "../../components/Common/Breadcrumb"

import {
  addHouseholdFileUpload as onAddHouseholdFileUpload,
  addHouseholdDataUpload as onAddHouseholdDataUpload,
} from "store/actions"

//redux
import { useSelector, useDispatch } from "react-redux"
import { createSelector } from "reselect"

import {
  Badge,
  Button,
  Col,
  Row,
  UncontrolledTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  Form,
  Input,
  FormFeedback,
  Label,
  Card,
  CardBody,
} from "reactstrap"

import Spinners from "components/Common/Spinner"
import { isEmpty } from "lodash"
import AlertContainer from "components/Common/AlertContainer"
import { CLEAR_HOUSEHOLD_STATE } from "store/household/actionTypes"

function BulkUpload() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [file, setFile] = useState(null)
  const [error, setError] = useState("")
  const [resultData, setResultData] = useState({})

  const { result, loading } = useSelector(state => state.household)
  const [isLoading, setLoading] = useState(loading)
  const { basicSetting } = useSelector(state => state.Layout)

  document.title =
    "Bulk Upload | " + (basicSetting?.SiteName || process.env.REACT_APP_NAME)

  useEffect(() => {}, [dispatch])

  const handleSubmit = e => {
    e.preventDefault()
    if (!file) {
      setError("Required")
      return
    }
    setError("")
    dispatch(onAddHouseholdFileUpload(file))
  }

  useEffect(() => {
    if (!isEmpty(result)) {
      setResultData(result.ResponseData)
    }
  }, [result])

  const handleDownloadTemplate = () => {
    const link = document.createElement("a")
    link.href = `${process.env.REACT_APP_API_ROOT_URL}/_files/Template/digital_inclusion_template.xlsx`
    link.setAttribute("download", "template.xlsx")
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs
            title="Bulk Upload"
            breadcrumbItem="Household Bulk Upload"
          />
          {isLoading ? (
            <Spinners setLoading={setLoading} />
          ) : (
            <Row>
              <Col xs="12">
                <Card>
                  <CardBody>
                    <div hidden={!isEmpty(resultData)}>
                      <AlertContainer
                        message={result?.ErrorMessage}
                        color="warning"
                      />

                      <form onSubmit={handleSubmit}>
                        <Row className="mb-3">
                          <Col xs="12" md="8">
                            <h5 className="mb-3">
                              The Excel file must be in the following format:
                            </h5>
                            <div className="fw-bold">
                              Month, Postcode, Children, Pensioners, Disabled,
                              Other, Footfall, Training Date, Venue, Type,
                              Subjects (comma-separated), Delivery
                            </div>
                            <ul>
                              <li>Header row should not be modified.</li>
                              <li>Worksheet name should not be changed.</li>
                              <li>
                                Month, Postcode, Children, Pensioners, Disabled,
                                Other, Training Date, Venue, Type are mandatory.
                              </li>
                              <li>Use format: MMM-YYYY for months</li>
                              <li>
                                Month, Type and Subjects must exist in the
                                system.
                              </li>
                            </ul>
                          </Col>
                          <Col
                            xs="12"
                            md="4"
                            className="text-md-end text-start mt-2 mt-md-0 d-flex align-items-start justify-content-md-end"
                          >
                            <Button
                              color="success"
                              onClick={handleDownloadTemplate}
                              className="btn-sm"
                            >
                              Download XLS Template
                            </Button>
                          </Col>
                        </Row>
                        <div className="mb-3" style={{ maxWidth: "400px" }}>
                          <Input
                            className="form-control"
                            type="file"
                            onChange={e => setFile(e.target.files[0])}
                          />
                          {error && (
                            <p style={{ color: "red", fontSize: "0.85rem" }}>
                              {error}
                            </p>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          <Button color="primary" className="btn">
                            Proceed
                          </Button>
                        </div>
                      </form>
                    </div>
                    <div hidden={isEmpty(resultData)}>
                      <h5>{"Rejected Records - " + result?.RejectedCount}</h5>
                      <div className="table-responsive">
                        <table className="table table-bordered table-striped table-hover">
                          <thead className="table-dark">
                            <tr>
                              <th>Month</th>
                              <th>Postcode</th>
                              <th>Children</th>
                              <th>Pensioners</th>
                              <th>Disabled</th>
                              <th>Other</th>
                              <th>Footfall</th>
                              <th>Training Date</th>
                              <th>Venue</th>
                              <th>Training Type</th>
                              <th>Subjects</th>
                              <th>Delivery</th>
                              <th>Result</th>
                              <th>Result Notes</th>
                            </tr>
                          </thead>
                          <tbody>
                            {resultData && resultData?.length > 0 ? (
                              resultData.map((row, i) => (
                                <tr key={i}>
                                  <td>{row.Month}</td>
                                  <td>{row.Postcode}</td>
                                  <td>{row.Children}</td>
                                  <td>{row.Pensioners}</td>
                                  <td>{row.Disabled}</td>
                                  <td>{row.Other}</td>
                                  <td>{row.Footfall}</td>
                                  <td>
                                    {Moment(
                                      row.TrainingDate,
                                      "DD-MM-YYYY"
                                    ).format("DD/MM/YYYY")}
                                  </td>
                                  <td>{row.Venue}</td>
                                  <td>{row.TrainingType}</td>
                                  <td>{row.Subjects}</td>
                                  <td>{row.Delivery}</td>
                                  <td>{row.Result}</td>
                                  <td>{row.ResultNotes}</td>
                                </tr>
                              ))
                            ) : (
                              <tr>
                                <td colSpan="12" className="text-center">
                                  No records found
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                      <div className="d-flex flex-wrap gap-2">
                        <Button
                          color="primary"
                          className="btn"
                          onClick={x => {
                            dispatch(onAddHouseholdDataUpload())
                            dispatch({ type: CLEAR_HOUSEHOLD_STATE })
                          }}
                          disabled={result?.RejectedCount > 0}
                        >
                          Proceed
                        </Button>
                        <Button
                          type="submit"
                          color="secondary"
                          onClick={x => {
                            setResultData({})
                            dispatch({ type: CLEAR_HOUSEHOLD_STATE })
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </React.Fragment>
  )
}

BulkUpload.propTypes = {
  preGlobalFilteredRows: PropTypes.any,
}

export default BulkUpload
